import React from 'react';
import type { GetFeaturesInnerParams } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/types.ts';
import TranslateSafeText from '@/app/common/components/TranslateSafeText';

export const getFlexibleDatesUSPMarkup = (
  params: GetFeaturesInnerParams,
  text?: string | null
): string | null | undefined => {
  const { isMobile, t, showFlexibleAlternativeDates, isOverlay } = params;
  const mobileUsp = t('vacation.oss.alternative_dates.usp_mobile');
  const desktopUsp = t('vacation.oss.alternative_dates.usp');

  if (showFlexibleAlternativeDates && !isOverlay) {
    return `${text} · <span class="more">${isMobile ? mobileUsp : desktopUsp}</span>`;
  }

  return text;
};

type DatesLabelParts = { date?: string; nights?: string | null; emphasiseDate?: boolean };

const getDatesLabelParts = (params: GetFeaturesInnerParams): DatesLabelParts => {
  const {
    nightsText,
    nightsTextStay,
    departureDate,
    checkinDate,
    isHO,
    isFlexible,
    isPartialStay,
    isOvernight,
    hasApexFlexibleDates,
  } = params;
  const { isOverlay, isMobile } = params;
  // The compact mobile map overlay uses the short form ("2 nights", no "at hotel").
  const nightsStayLabel = isOverlay && isMobile ? nightsText : nightsTextStay;
  // MPH-945: once the list mixes departure dates, the date is what separates one result from another,
  // so it has to read before the price.
  const emphasiseDate = hasApexFlexibleDates;

  // HO and DP alike. An open search can arrive without a date to lead with.
  if (isFlexible) return { date: departureDate, nights: nightsText };
  if (isHO) return {};
  if (isOvernight || isPartialStay) {
    return { date: checkinDate ?? departureDate, nights: nightsStayLabel, emphasiseDate };
  }
  return { date: departureDate, nights: nightsStayLabel, emphasiseDate };
};

// "13 Oct 2024 (2 nights at hotel)", or "2 nights at hotel" without a date. Feature text is rendered as markup.
export const getDatesOnlyLabel = (params: GetFeaturesInnerParams): string | null | undefined => {
  const { date, nights, emphasiseDate } = getDatesLabelParts(params);
  if (!date) return nights;

  return `${emphasiseDate ? `<b>${date}</b>` : date} (${nights})`;
};

export const renderDatesLabelWithBoldNights = (params: GetFeaturesInnerParams): React.ReactNode => {
  const { date, nights, emphasiseDate } = getDatesLabelParts(params);

  return (
    <>
      {emphasiseDate ? <b>{date}</b> : <TranslateSafeText>{date}</TranslateSafeText>} <b>{`(${nights})`}</b>
    </>
  );
};

// renderDatesLabelWithBoldNights as markup, for a row that has no tooltip to wrap it.
export const getDatesLabelWithBoldNightsMarkup = (params: GetFeaturesInnerParams): string => {
  const { date, nights, emphasiseDate } = getDatesLabelParts(params);

  return `${emphasiseDate ? `<b>${date ?? ''}</b>` : (date ?? '')} <b>(${nights})</b>`;
};
